import { TagColorVariant } from '@ben/orion/constants/display-status';
import DisplayStatusTag from '@ben/orion/display-status-tag';
import type { ITheme } from '@ben/orion/utils/theme';
import styled, { css } from 'styled-components';

/**
 * Every status the workspace shows, and everything it shows about them.
 *
 * One row per status with every column filled, closed with `satisfies`, so a
 * new status is a compile error until its label and tone are decided — and for
 * a requirement, its dot colour, which counts key it reads and how pressing it
 * is. The maps this replaced were separate and half-populated: run and change
 * statuses had a tone but no label, so the tag fell through to rendering the
 * raw enum value and an IM read `PARTIALLY_COMPLETE` off the screen.
 *
 * The three sets share no values, so one lookup serves all of them without a
 * discriminator.
 */

interface StatusMeta {
  label: string;
  tone: TagColorVariant;
}

/**
 * A tally of requirements by status. Declared here rather than with the API
 * types so the registry below can key off it: the statuses that exist and the
 * fields that count them are one decision, and this is where it is made.
 */
export type StatusCounts = { total: number } & Record<
  'dirty' | 'proposed' | 'outOfScope' | 'clean',
  number
>;

/** Declared in the order the filter chips read. */
export const REQUIREMENT_STATUSES = [
  'DIRTY',
  'PROPOSED',
  'OUT_OF_SCOPE',
  'CLEAN',
] as const;
export type RequirementStatus = (typeof REQUIREMENT_STATUSES)[number];

interface RequirementStatusMeta extends StatusMeta {
  dot: (theme: ITheme) => string;
  /** Which field of the API's tally counts this status. */
  countKey: Exclude<keyof StatusCounts, 'total'>;
  /**
   * Lower is more pressing. Deliberately not the declaration order: a domain
   * heading shows its most pressing requirement's dot, where an unanswered
   * requirement outranks one already awaiting a decision, while the filters
   * put the two an IM can act on first.
   */
  urgency: number;
}

const REQUIREMENT_STATUS = {
  DIRTY: {
    label: 'Needs configuring',
    tone: TagColorVariant.Error,
    dot: (theme) => theme.palette.error.main,
    countKey: 'dirty',
    urgency: 0,
  },
  PROPOSED: {
    label: 'Awaiting decision',
    tone: TagColorVariant.Warning,
    dot: (theme) => theme.palette.warning.main,
    countKey: 'proposed',
    urgency: 2,
  },
  OUT_OF_SCOPE: {
    label: 'Unanswered',
    tone: TagColorVariant.Neutral,
    dot: (theme) => theme.palette.neutral[60],
    countKey: 'outOfScope',
    urgency: 1,
  },
  CLEAN: {
    label: 'Configured',
    tone: TagColorVariant.Success,
    dot: (theme) => theme.palette.success.main,
    countKey: 'clean',
    urgency: 3,
  },
} satisfies Record<RequirementStatus, RequirementStatusMeta>;

export const RUN_STATUSES = [
  'ACTIVE',
  'COMPLETE',
  'PARTIALLY_COMPLETE',
  'CANCELLED',
  'FAILED',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

interface RunStatusMeta extends StatusMeta {
  /** Whether the run can still change, and so whether polling is worth it. */
  live: boolean;
}

const RUN_STATUS = {
  ACTIVE: { label: 'Running', tone: TagColorVariant.Warning, live: true },
  COMPLETE: { label: 'Complete', tone: TagColorVariant.Success, live: false },
  PARTIALLY_COMPLETE: {
    label: 'Partly complete',
    tone: TagColorVariant.Warning,
    live: false,
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: TagColorVariant.Neutral,
    live: false,
  },
  FAILED: { label: 'Failed', tone: TagColorVariant.Error, live: false },
} satisfies Record<RunStatus, RunStatusMeta>;

export const CHANGE_STATUSES = [
  'PENDING',
  'APPLIED',
  'REJECTED',
  'SUPERSEDED',
] as const;
export type ChangeStatus = (typeof CHANGE_STATUSES)[number];

const CHANGE_STATUS = {
  PENDING: { label: 'Awaiting decision', tone: TagColorVariant.Warning },
  APPLIED: { label: 'Applied', tone: TagColorVariant.Success },
  REJECTED: { label: 'Rejected', tone: TagColorVariant.Neutral },
  SUPERSEDED: { label: 'Superseded', tone: TagColorVariant.Neutral },
} satisfies Record<ChangeStatus, StatusMeta>;

export type WorkspaceStatus = RequirementStatus | RunStatus | ChangeStatus;

const STATUS: Record<WorkspaceStatus, StatusMeta> = {
  ...REQUIREMENT_STATUS,
  ...RUN_STATUS,
  ...CHANGE_STATUS,
};

const isRequirementStatus = (value: unknown): value is RequirementStatus =>
  REQUIREMENT_STATUSES.includes(value as RequirementStatus);

/**
 * The status filter as read off the URL, which can say anything at all. An
 * unrecognised one falls back to `all` rather than being forwarded to the API,
 * which answers a bad status with a 400.
 */
export const parseStatusFilter = (
  value: string | null | undefined
): RequirementStatus | 'all' => (isRequirementStatus(value) ? value : 'all');

/** Filter chips for a requirement list, `all` first. */
export const REQUIREMENT_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  ...REQUIREMENT_STATUSES.map((status) => ({
    value: status,
    label: REQUIREMENT_STATUS[status].label,
  })),
];

/** The count beside each filter chip, straight off the API's tally. */
export const requirementChipCounts = (
  counts: StatusCounts
): Record<string, number> => ({
  all: counts.total,
  ...Object.fromEntries(
    REQUIREMENT_STATUSES.map((status) => [
      status,
      counts[REQUIREMENT_STATUS[status].countKey],
    ])
  ),
});

/** The status a domain heading shows: the most pressing one it contains. */
export const mostPressingStatus = (
  statuses: RequirementStatus[]
): RequirementStatus =>
  statuses.reduce<RequirementStatus>(
    (pressing, status) =>
      REQUIREMENT_STATUS[status].urgency < REQUIREMENT_STATUS[pressing].urgency
        ? status
        : pressing,
    'CLEAN'
  );

export const isLiveRunStatus = (status: RunStatus | undefined) =>
  status !== undefined && RUN_STATUS[status].live;

export const StatusTag = ({ status }: { status: WorkspaceStatus }) => (
  <DisplayStatusTag
    displayStatus={{ text: STATUS[status].label, type: STATUS[status].tone }}
  />
);

/** The same colour language as StatusTag, for use inline in a dense list. */
export const StatusDot = styled.span<{ $status: RequirementStatus }>`
  ${({ theme, $status }) => css`
    display: inline-block;
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${REQUIREMENT_STATUS[$status].dot(theme)};
  `}
`;
